import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { resolveApiUrl } from './api-url';
import { setActiveTenant, clearActiveTenant, readCachedTenantState, writeCachedTenantState } from '@/lib/active-tenant';
import { TenantContext, type TenantContextValue, type TenantSummary } from './tenant-context';

const API_URL = resolveApiUrl();

interface TenantState {
  activeTenant: TenantSummary | null;
  tenants: TenantSummary[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseTenantSummary(value: unknown): TenantSummary | null {
  if (!isRecord(value)) {
    return null;
  }
  const { slug, name, logoUrl } = value;
  if (typeof slug !== 'string' || slug.length === 0) {
    return null;
  }
  return {
    slug,
    name: typeof name === 'string' && name.length > 0 ? name : slug,
    logoUrl: typeof logoUrl === 'string' ? logoUrl : null,
  };
}

/**
 * Извлекает tenant/tenants из произвольного /me-ответа.
 * Если backend не присылает эти поля (однотенантный режим) — возвращает пустое состояние.
 */
function parseTenantState(meData: unknown): TenantState {
  if (!isRecord(meData)) {
    return { activeTenant: null, tenants: [] };
  }

  const tenants: TenantSummary[] = [];
  if (Array.isArray(meData.tenants)) {
    for (const entry of meData.tenants) {
      const parsed = parseTenantSummary(entry);
      if (parsed) {
        tenants.push(parsed);
      }
    }
  }

  const activeSlug = typeof meData.tenant === 'string' ? meData.tenant : null;
  const active =
    (activeSlug ? tenants.find((t) => t.slug === activeSlug) : undefined) ??
    (activeSlug ? { slug: activeSlug, name: activeSlug, logoUrl: null } : null) ??
    null;

  return { activeTenant: active, tenants };
}

async function fetchTenantState(): Promise<TenantState> {
  const token = localStorage.getItem('accessToken');
  if (!token) {
    return { activeTenant: null, tenants: [] };
  }

  try {
    // Глобальный fetch-интерцептор сам проставит Authorization + X-Tenant-Id.
    const response = await fetch(`${API_URL}/auth/admin/me`);
    if (!response.ok) {
      return { activeTenant: null, tenants: [] };
    }
    const json: unknown = await response.json();
    if (isRecord(json) && json.success === true) {
      return parseTenantState(json.data);
    }
    return { activeTenant: null, tenants: [] };
  } catch {
    return { activeTenant: null, tenants: [] };
  }
}

interface TenantProviderProps {
  children: ReactNode;
}

/**
 * Поставляет активный tenant и список доступных БД из /auth/admin/me.
 *
 * Однотенантный режим (backend не присылает tenant/tenants): activeTenant=null,
 * tenants=[], hasMultipleTenants=false — UI идентичен до-tenant версии.
 */
function initialStateFromCache(): TenantState {
  const cached = readCachedTenantState();
  const activeTenant = cached.activeSlug
    ? cached.tenants.find((t) => t.slug === cached.activeSlug) ?? {
        slug: cached.activeSlug,
        name: cached.activeSlug,
        logoUrl: null,
      }
    : null;
  return { activeTenant, tenants: cached.tenants };
}

export function TenantProvider({ children }: TenantProviderProps) {
  // Инициализируемся из localStorage-кэша, чтобы переключатель БД был виден сразу
  // после рефреша, не дожидаясь /auth/admin/me (иначе он «пропадает в моменте»).
  const [state, setState] = useState<TenantState>(initialStateFromCache);
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async (): Promise<boolean> => {
      const next = await fetchTenantState();
      if (!mounted) {
        return false;
      }
      // Пустой ответ (нет токена / гонка при старте) НЕ затирает уже показанное
      // из кэша состояние — иначе переключатель мигнёт и исчезнет.
      if (next.tenants.length === 0 && !next.activeTenant) {
        return false;
      }
      setState(next);
      writeCachedTenantState({
        activeSlug: next.activeTenant?.slug ?? null,
        tenants: next.tenants,
      });
      if (next.activeTenant) {
        setActiveTenant(next.activeTenant.slug);
      } else {
        clearActiveTenant();
      }
      return next.tenants.length > 0;
    };

    // Провайдер монтируется при входе в админку, но accessToken логин/refresh
    // записывает асинхронно — первый load() может уйти без токена и вернуть пусто,
    // из-за чего переключатель БД не появлялся до перезахода. Повторяем, пока не
    // получим тенанты (или не выйдет лимит попыток).
    // ponytail: короткий поллинг вместо подписки на auth-события — их тут нет,
    // а storage-event не стреляет во вкладке-источнике записи токена.
    let attempts = 0;
    const tryLoad = async () => {
      const ok = await load();
      attempts += 1;
      if (mounted && !ok && attempts < 10) {
        timer = window.setTimeout(tryLoad, 500);
      }
    };
    let timer = window.setTimeout(tryLoad, 0);

    return () => {
      mounted = false;
      window.clearTimeout(timer);
    };
  }, []);

  const switchTenant = useCallback(async (slug: string) => {
    if (slug === state.activeTenant?.slug) {
      return;
    }
    setIsSwitching(true);
    try {
      const response = await fetch(`${API_URL}/auth/admin/switch-tenant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant: slug }),
        credentials: 'include',
      });

      const json: unknown = await response.json().catch(() => null);
      if (response.ok && isRecord(json) && json.success === true && isRecord(json.data)) {
        const accessToken = json.data.accessToken;
        if (typeof accessToken === 'string' && accessToken.length > 0) {
          localStorage.setItem('accessToken', accessToken);
        }
      }

      // Записываем активный tenant до reload, чтобы первый запрос после перезагрузки
      // ушёл с верным X-Tenant-Id, а login-подсказка указывала на ту же БД.
      setActiveTenant(slug);

      // Полный reload — гарантия отсутствия смешанного кэша TanStack/Refine (см. 04-admin.md §4.1).
      window.location.reload();
    } finally {
      setIsSwitching(false);
    }
  }, [state.activeTenant?.slug]);

  const value = useMemo<TenantContextValue>(() => ({
    activeTenant: state.activeTenant,
    tenants: state.tenants,
    hasMultipleTenants: state.tenants.length > 1,
    switchTenant,
    isSwitching,
  }), [state.activeTenant, state.tenants, switchTenant, isSwitching]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}
